import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";
import { toast } from "react-toastify";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const TYPES = [
  { key: "winner",   label: "Winner",   color: "#10b981", desc: "Sent when a nominee is marked as Winner" },
  { key: "honorary", label: "Honorary", color: "#0891b2", desc: "Sent when a nominee is marked as Honorary" },
  { key: "rejected", label: "Rejected", color: "#ef4444", desc: "Sent when a nominee is marked as Rejected" },
];

const PLACEHOLDERS = [
  { key: "{{fullName}}",           label: "Full Name" },
  { key: "{{nominationType}}",     label: "Nomination Type" },
  { key: "{{nominationCategory}}", label: "Category" },
  { key: "{{nominationSubCategory}}", label: "Sub-category" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

export default function AdminEmailTemplatesPage() {
  const { eventId } = useParams();
  const [tab, setTab]               = useState("winner");
  const [templates, setTemplates]   = useState({});
  const [saving, setSaving]         = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sourceMode,    setSourceMode]    = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    api.get(`/email-templates/event/${eventId}`)
      .then(res => {
        const map = {};
        (res.data.data || []).forEach(t => { map[t.type] = { subject: t.subject, htmlBody: t.htmlBody }; });
        setTemplates(map);
      })
      .catch(() => toast.error("Failed to load templates"));
  }, [eventId]);

  const current = templates[tab] || { subject: "", htmlBody: "" };

  const setField = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [tab]: { ...(prev[tab] || { subject: "", htmlBody: "" }), [field]: value },
    }));
  };

  const insertPlaceholder = (placeholder) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, placeholder);
      editor.setSelection(range.index + placeholder.length);
    } else {
      setField("subject", current.subject + placeholder);
    }
  };

  const handleSave = async () => {
    const bodyText = current.htmlBody?.replace(/<[^>]*>/g, "").trim() ?? "";
    if (!current.subject.trim() || !bodyText) {
      toast.error("Subject and body are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/email-templates", {
        eventId,
        type: tab,
        subject: current.subject,
        htmlBody: current.htmlBody,
      });
      toast.success("Template saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/email-templates/event/${eventId}/type/${tab}`);
      setTemplates(prev => { const n = { ...prev }; delete n[tab]; return n; });
      setConfirmDelete(false);
      toast.success("Template deleted");
    } catch { toast.error("Delete failed"); }
  };

  const activeType = TYPES.find(t => t.key === tab);

  return (
    <Layout title="Email Templates">

      {/* Type tabs */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
        {TYPES.map(t => {
          const hasTpl = !!templates[t.key]?.htmlBody;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "8px 22px", borderRadius: 8, border: "2px solid",
                borderColor: tab === t.key ? t.color : "#e5e7eb",
                background: tab === t.key ? t.color : "#fff",
                color: tab === t.key ? "#fff" : "#374151",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 7,
              }}
            >
              {t.label}
              {hasTpl && (
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: tab === t.key ? "rgba(255,255,255,0.8)" : t.color, display: "inline-block" }} />
              )}
            </button>
          );
        })}
      </div>

      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20, marginTop: -12 }}>{activeType?.desc}</p>

      {/* Placeholder chips */}
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Insert:</span>
        {PLACEHOLDERS.map(p => (
          <button
            key={p.key}
            onClick={() => insertPlaceholder(p.key)}
            title={`Insert ${p.key}`}
            style={{ background: "#f0f9ff", border: "1px solid #bae6fd", color: "#0369a1", padding: "4px 11px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>

        {/* Subject */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Subject Line</label>
          <input
            type="text"
            value={current.subject}
            onChange={e => setField("subject", e.target.value)}
            placeholder="e.g. Congratulations! You have been selected as {{nominationCategory}} Winner"
            style={{ width: "100%", padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box", outline: "none" }}
          />
        </div>

        {/* Rich text body */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label style={labelStyle}>Email Body</label>
            <button
              type="button"
              onClick={() => setSourceMode(v => !v)}
              style={{ fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 6, border: "1px solid #e5e7eb", background: sourceMode ? "#0f172a" : "#f9fafb", color: sourceMode ? "#fff" : "#374151", cursor: "pointer" }}
            >
              {sourceMode ? "◀ Visual" : "</> Source HTML"}
            </button>
          </div>
          {sourceMode ? (
            <textarea
              value={current.htmlBody}
              onChange={e => setField("htmlBody", e.target.value)}
              style={{ width: "100%", minHeight: 380, padding: 14, border: "1px solid #e5e7eb", borderRadius: 8, fontFamily: "'Courier New', monospace", fontSize: 12, lineHeight: 1.6, color: "#1e293b", background: "#f8fafc", boxSizing: "border-box", resize: "vertical" }}
              placeholder="Paste raw HTML here…"
              spellCheck={false}
            />
          ) : (
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={current.htmlBody}
                onChange={v => setField("htmlBody", v)}
                modules={QUILL_MODULES}
                style={{ minHeight: 320 }}
                placeholder="Write your email content here. Use the Insert buttons above to add dynamic placeholders…"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: activeType?.color, color: "#fff", border: "none", padding: "10px 28px", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer", opacity: saving ? 0.6 : 1 }}
          >
            {saving ? "Saving…" : "Save Template"}
          </button>
          <button
            onClick={() => setPreviewing(p => !p)}
            style={{ background: "#f3f4f6", color: "#374151", border: "none", padding: "10px 20px", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            {previewing ? "Hide Preview" : "Preview"}
          </button>
          {templates[tab]?.htmlBody && (
            confirmDelete ? (
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 14px" }}>
                <span style={{ fontSize: 13, color: "#dc2626", fontWeight: 600 }}>Delete this template?</span>
                <button onClick={handleDelete} style={{ background: "#dc2626", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Yes, Delete</button>
                <button onClick={() => setConfirmDelete(false)} style={{ background: "none", border: "1px solid #fecaca", color: "#dc2626", padding: "6px 12px", borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{ marginLeft: "auto", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "10px 20px", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer" }}
              >
                Delete Template
              </button>
            )
          )}
        </div>
      </div>

      {/* Preview panel */}
      {previewing && current.htmlBody && (
        <div style={{ marginTop: 20, background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>
            Preview — placeholders shown as-is (replaced with real data on send)
          </p>
          <div style={{ borderBottom: "1px solid #f3f4f6", paddingBottom: 12, marginBottom: 16, fontSize: 14 }}>
            <span style={{ color: "#6b7280", fontWeight: 600 }}>Subject: </span>
            <span style={{ color: "#111827" }}>{current.subject || "—"}</span>
          </div>
          <div
            style={{ fontSize: 14, lineHeight: 1.7, color: "#111827" }}
            dangerouslySetInnerHTML={{ __html: current.htmlBody }}
          />
        </div>
      )}
    </Layout>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.04em" };
